import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  GraduationCap, BookOpen, Heart, Umbrella, Car, Briefcase,
  ChevronRight, Trophy, Star, ArrowLeft, Users, CheckCircle2,
  Lock, Flame, Target, Award, Loader2, BarChart3, Shield
} from "lucide-react";
import { authGet, isAdvisor, isAuthenticated } from "../utils/auth";
import MobileHeader from "../components/MobileHeader";
import MobileBottomNav from "../components/MobileBottomNav";
import MobileSidebar from "../components/MobileSidebar";

const ICON_MAP = {
  foundation: BookOpen,
  heart: Heart,
  umbrella: Umbrella,
  car: Car,
  briefcase: Briefcase,
};

const LEVEL_LABELS = [
  { min: 0, max: 0, label: "Not Started", color: "text-[#3A4557]", bg: "bg-[#EDF1F8]/10" },
  { min: 1, max: 30, label: "Beginner", color: "text-[#2E3B57]", bg: "bg-[#EDF1F8]/30" },
  { min: 31, max: 70, label: "Intermediate", color: "text-[#A87608]", bg: "bg-[#D4A017]/10" },
  { min: 71, max: 99, label: "Advanced", color: "text-purple-600", bg: "bg-purple-500/10" },
  { min: 100, max: 100, label: "Expert", color: "text-emerald-600", bg: "bg-emerald-500/10" },
];

function getLevel(pct) {
  return LEVEL_LABELS.find(l => pct >= l.min && pct <= l.max) || LEVEL_LABELS[0];
}

function CircularProgress({ percent, size = 56, stroke = 4, color }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (percent / 100) * circ;
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="currentColor" strokeWidth={stroke} className="text-[#DCE4F0]" />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        className="transition-all duration-700" />
    </svg>
  );
}

export default function TrainingAcademy() {
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tab, setTab] = useState("modules"); // modules | admin
  const [adminData, setAdminData] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const isAdmin = isAdvisor();

  useEffect(() => {
    if (!isAuthenticated()) { navigate("/advisor/login"); return; }
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      const res = await authGet("/api/training/modules");
      if (res.ok) setModules(res.data.modules || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchAdminData = async () => {
    if (adminData) return;
    setAdminLoading(true);
    try {
      const res = await authGet("/api/training/admin/progress");
      if (res.ok) setAdminData(res.data);
    } catch (e) { console.error(e); }
    setAdminLoading(false);
  };

  const handleTabSwitch = (t) => {
    setTab(t);
    if (t === "admin") fetchAdminData();
  };

  const totalChapters = modules.reduce((s, m) => s + m.total_chapters, 0);
  const completedChapters = modules.reduce((s, m) => s + m.completed_chapters, 0);
  const overallPct = totalChapters > 0 ? Math.round((completedChapters / totalChapters) * 100) : 0;
  const quizzesPassed = modules.filter(m => m.quiz_passed).length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8]">
      <MobileHeader title="Training Academy" onMenuClick={() => setSidebarOpen(true)} />
      <MobileSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="max-w-5xl mx-auto px-4 py-6 pb-32">
        {/* Back button - desktop */}
        <div className="hidden md:flex items-center gap-3 mb-6">
          <button onClick={() => navigate("/advisor/dashboard")} className="text-[#3A4557] hover:text-[#1B2A4A] transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <GraduationCap className="h-6 w-6 text-[#A87608]" />
            <h1 className="text-2xl font-bold text-[#1B2A4A]">Insurance Training Academy</h1>
          </div>
        </div>

        {/* Admin Tabs */}
        {isAdmin && (
          <div className="flex gap-2 mb-6" data-testid="training-tabs">
            <button onClick={() => handleTabSwitch("modules")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === "modules" ? "bg-[#D4A017]/15 text-[#A87608] border border-[#D4A017]/30" : "bg-[#EDF1F8]/50 text-[#3A4557] border border-[#C9D4E6]/50 hover:text-[#1B2A4A]"}`}
              data-testid="tab-modules">
              <BookOpen className="h-4 w-4 inline mr-1.5" />Modules
            </button>
            <button onClick={() => handleTabSwitch("admin")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === "admin" ? "bg-[#D4A017]/15 text-[#A87608] border border-[#D4A017]/30" : "bg-[#EDF1F8]/50 text-[#3A4557] border border-[#C9D4E6]/50 hover:text-[#1B2A4A]"}`}
              data-testid="tab-admin-progress">
              <Users className="h-4 w-4 inline mr-1.5" />Employee Progress
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 text-[#A87608] animate-spin" />
          </div>
        ) : tab === "modules" ? (
          <ModulesView modules={modules} overallPct={overallPct} completedChapters={completedChapters}
            totalChapters={totalChapters} quizzesPassed={quizzesPassed} navigate={navigate} />
        ) : (
          <AdminProgressView data={adminData} loading={adminLoading} modules={modules} />
        )}
      </div>
      <MobileBottomNav onMenuClick={() => setSidebarOpen(true)} />
    </div>
  );
}

/* ============ MODULES VIEW ============ */
function ModulesView({ modules, overallPct, completedChapters, totalChapters, quizzesPassed, navigate }) {
  const level = getLevel(overallPct);
  const streakDays = completedChapters > 0 ? Math.min(completedChapters, 7) : 0;

  return (
    <>
      {/* Stats Banner */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-[#E7EDF6]/80 to-[#EDF1F8]/80 border border-[#C9D4E6]/50 rounded-2xl p-5 mb-6 backdrop-blur-sm">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-[#3A4557] text-xs uppercase tracking-wider mb-1">Your Progress</p>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-3xl font-bold text-[#1B2A4A]">{overallPct}%</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${level.bg} ${level.color} font-medium`}>
                {level.label}
              </span>
            </div>
            <div className="w-full bg-[#E2E9F4]/50 rounded-full h-2 mb-3">
              <motion.div className="h-2 rounded-full bg-gradient-to-r from-[#D4A017] to-[#D9A82C]"
                initial={{ width: 0 }} animate={{ width: `${overallPct}%` }} transition={{ duration: 0.8, delay: 0.2 }} />
            </div>
            <div className="flex gap-4 text-xs text-[#3A4557]">
              <span><CheckCircle2 className="h-3.5 w-3.5 inline text-emerald-600 mr-1" />{completedChapters}/{totalChapters} Chapters</span>
              <span><Trophy className="h-3.5 w-3.5 inline text-[#A87608] mr-1" />{quizzesPassed}/{modules.length} Quizzes Passed</span>
            </div>
          </div>
          <div className="relative ml-4 hidden sm:block">
            <CircularProgress percent={overallPct} size={72} stroke={5} color="#f59e0b" />
            <div className="absolute inset-0 flex items-center justify-center">
              <GraduationCap className="h-6 w-6 text-[#A87608]" />
            </div>
          </div>
        </div>
        {/* Streak */}
        {streakDays > 0 && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#C9D4E6]/50">
            <Flame className="h-4 w-4 text-orange-600" />
            <span className="text-xs text-orange-700 font-medium">{streakDays}-day learning streak!</span>
            <div className="flex gap-1 ml-auto">
              {[...Array(7)].map((_, i) => (
                <div key={i} className={`w-3 h-3 rounded-sm ${i < streakDays ? "bg-orange-400" : "bg-[#E2E9F4]"}`} />
              ))}
            </div>
          </div>
        )}
      </motion.div>

      {/* Module Cards */}
      <div className="space-y-4">
        {modules.map((mod, idx) => (
          <ModuleCard key={mod.id} mod={mod} idx={idx} navigate={navigate} />
        ))}
      </div>
    </>
  );
}

function ModuleCard({ mod, idx, navigate }) {
  const Icon = ICON_MAP[mod.icon] || BookOpen;
  const level = getLevel(mod.progress_pct);
  const isLocked = idx > 0 && false; // no locking for now

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.08 }}
      onClick={() => !isLocked && navigate(`/advisor/training/${mod.id}`)}
      className={`group cursor-pointer rounded-2xl border transition-all duration-300 ${
        isLocked
          ? "bg-white/50 border-[#C9D4E6] opacity-60"
          : "bg-[#EDF1F8]/40 border-[#C9D4E6]/50 hover:border-[#D4A017]/30 hover:bg-[#EDF1F8]/70"
      }`}
      data-testid={`module-card-${mod.id}`}
    >
      <div className="p-5">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${mod.color}15`, border: `1px solid ${mod.color}30` }}>
            {isLocked ? <Lock className="h-5 w-5 text-[#3A4557]" /> : <Icon className="h-5 w-5" style={{ color: mod.color }} />}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-xs font-medium uppercase tracking-wider" style={{ color: mod.color }}>
                Module {mod.order} — {mod.subtitle}
              </span>
            </div>
            <h3 className="text-lg font-semibold text-[#1B2A4A] group-hover:text-[#A87608] transition-colors truncate">
              {mod.title}
            </h3>
            <p className="text-[#3A4557] text-sm mt-1 line-clamp-2">{mod.description}</p>

            {/* Progress Row */}
            <div className="flex items-center gap-3 mt-3">
              <div className="flex-1 bg-[#E2E9F4]/50 rounded-full h-1.5">
                <div className="h-1.5 rounded-full transition-all duration-500"
                  style={{ width: `${mod.progress_pct}%`, backgroundColor: mod.color }} />
              </div>
              <span className="text-xs text-[#3A4557] whitespace-nowrap">{mod.completed_chapters}/{mod.total_chapters}</span>
            </div>

            {/* Badges */}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className={`text-xs px-2 py-0.5 rounded-full ${level.bg} ${level.color}`}>{level.label}</span>
              {mod.quiz_passed && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 flex items-center gap-1">
                  <Trophy className="h-3 w-3" />Quiz Passed ({mod.quiz_score}%)
                </span>
              )}
              {mod.quiz_score !== null && !mod.quiz_passed && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-600 flex items-center gap-1">
                  <Target className="h-3 w-3" />Quiz: {mod.quiz_score}% (Need 70%)
                </span>
              )}
            </div>
          </div>

          {/* Arrow */}
          <ChevronRight className="h-5 w-5 text-[#3A4557] group-hover:text-[#A87608] transition-colors shrink-0 mt-2" />
        </div>
      </div>
    </motion.div>
  );
}

/* ============ ADMIN PROGRESS VIEW ============ */
function AdminProgressView({ data, loading, modules }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 text-[#A87608] animate-spin" />
      </div>
    );
  }

  if (!data || !data.employees || data.employees.length === 0) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        className="text-center py-16 bg-[#EDF1F8]/30 rounded-2xl border border-[#C9D4E6]/50">
        <Users className="h-12 w-12 text-[#3A4557] mx-auto mb-3" />
        <p className="text-[#3A4557] text-lg font-medium">No Training Activity Yet</p>
        <p className="text-[#3A4557] text-sm mt-1">Employee progress will appear here once they start learning</p>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
      <div className="bg-[#EDF1F8]/40 border border-[#C9D4E6]/50 rounded-2xl p-5 mb-4">
        <h3 className="text-[#1B2A4A] font-semibold mb-1 flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-[#A87608]" />Employee Training Dashboard
        </h3>
        <p className="text-[#3A4557] text-sm">Track employee learning progress and quiz scores</p>
      </div>

      {data.employees.map((emp, idx) => (
        <EmployeeProgressCard key={emp.user_id} emp={emp} idx={idx} modules={modules} totalChapters={data.total_chapters} totalModules={data.total_modules} />
      ))}
    </motion.div>
  );
}

function EmployeeProgressCard({ emp, idx, modules, totalChapters, totalModules }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.06 }}
      className="bg-[#EDF1F8]/40 border border-[#C9D4E6]/50 rounded-xl overflow-hidden"
      data-testid={`employee-progress-${emp.user_id}`}
    >
      <button onClick={() => setExpanded(!expanded)} className="w-full p-4 flex items-center gap-4 text-left hover:bg-[#EDF1F8]/60 transition-colors">
        {/* Avatar */}
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4A017] to-[#D9A82C] flex items-center justify-center shrink-0">
          <span className="text-[#1B2A4A] font-bold text-sm">{emp.name?.charAt(0) || "E"}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[#1B2A4A] font-medium truncate">{emp.name}</p>
          <div className="flex items-center gap-3 text-xs text-[#3A4557] mt-0.5">
            <span>{emp.chapters_completed}/{totalChapters} chapters</span>
            <span>{emp.quizzes_passed}/{totalModules} quizzes passed</span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <span className="text-xl font-bold text-[#A87608]">{emp.progress_pct}%</span>
        </div>
        <ChevronRight className={`h-4 w-4 text-[#3A4557] transition-transform ${expanded ? "rotate-90" : ""}`} />
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
            <div className="px-4 pb-4 border-t border-[#C9D4E6]/50 pt-3">
              <p className="text-xs text-[#3A4557] uppercase tracking-wider mb-2">Quiz Results</p>
              {emp.quiz_details && emp.quiz_details.length > 0 ? (
                <div className="space-y-2">
                  {emp.quiz_details.map((q, i) => {
                    const modName = modules.find(m => m.id === q.module_id)?.title || q.module_id;
                    return (
                      <div key={i} className="flex items-center justify-between bg-white/50 rounded-lg p-3">
                        <span className="text-sm text-[#2E3B57]">{modName}</span>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${q.passed ? "text-emerald-600" : "text-red-600"}`}>
                            {q.score}%
                          </span>
                          {q.passed ? (
                            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                          ) : (
                            <Target className="h-4 w-4 text-red-600" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[#3A4557] text-sm">No quizzes attempted yet</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
